import React, { useCallback, useRef } from 'react';
import { FormHandles } from '@unform/core';
import * as Yup from 'yup';
import { useNavigate, useLocation } from 'react-router-dom';

import api from '../../../services/api';

import { useToast } from '../../../hooks/toast';
import getValidationErrors from '../../../utils/getValidationErrors';

import FormProjectSurvey from '../Form';

interface Accesses {
  name: string;
  placeholders: string[];
  segments: string[];
}

interface Survey {
  name: string;
  title: string;
  year: number;
  pages: object[];
  isTemplate: boolean;
  isIntegrate: boolean;
  isDailyIntegration: boolean;
  projectId: number;
  productId: number;
  subGroup: string;
  defaultLanguageId: number;
}

interface SurveyFormData {
  name: string;
  title: string;
  product: number;
  project: number;
  subGroup: string;
  useTemplate: boolean;
  isIntegrate: boolean;
  isDailyIntegration: boolean;
  languageId: number;
  year: number;
}

interface Language {
  languageId: number;
  name: string;
}

interface Product {
  productId: number;
  name: string;
  userGroups: UserGroup[];
}

interface UserGroup {
  name: string;
  subGroups: SubGroup[];
  structure: object[];
}

interface SubGroup {
  name: string;
  surveyId?: number;
}
interface State {
  projectId: string;
}

const CreateProjectSurvey: React.FC = () => {
  const formRef = useRef<FormHandles>(null);
  const { addToast } = useToast();
  const navigate = useNavigate();
  const location = useLocation();
  const state = location.state as State;

  const handleSubmit = useCallback(
    async (data: SurveyFormData) => {
      try {
        formRef.current?.setErrors({});

        let pages: any = [];
        let accessesFromTemplate: Accesses[] = [];
        if (data.useTemplate) {
          const response = await api.get(`/products/${data.product}`);

          const product: Product = response.data;
          const subGroup = product?.userGroups
            .map((user: UserGroup) => {
              return user.subGroups.find(e => e.name === data.subGroup);
            })
            .filter(item => {
              if (item) return true;
              return false;
            })[0];

          if (subGroup?.surveyId) {
            const resp = await api.get(`surveys/${subGroup?.surveyId}`);
            pages = resp.data.pages;
            const responseGetAccessesFromTemplate = await api.get(
              `/accesses/survey/${subGroup?.surveyId}`,
            );
            accessesFromTemplate = responseGetAccessesFromTemplate.data;
          }
        }

        if (!pages || pages.length === 0) {
          pages = [{ name: 'page1', elements: [] }];
        }

        const survey: Survey = {
          name: data.name,
          title: data.title,
          year: data.year,
          pages,
          isTemplate: false,
          isIntegrate: data.isIntegrate,
          isDailyIntegration: data.isDailyIntegration,
          projectId: state?.projectId ? Number(state.projectId) : data.project,
          productId: data.product,
          subGroup: data.subGroup,
          defaultLanguageId: 1,
        };

        const schema = Yup.object().shape({
          projectId: Yup.number().required('The project is required'),
          name: Yup.string().required('The name is required'),
          subGroup: Yup.string().required('The user group is required'),
        });

        await schema.validate(survey, {
          abortEarly: false,
        });

        const responseSurvey = await api.post('/surveys', survey);

        if (
          responseSurvey.data.surveyId &&
          data.useTemplate &&
          accessesFromTemplate.length > 0
        ) {
          accessesFromTemplate.forEach(async access => {
            const accessPost = {
              name: access.name,
              surveyId: responseSurvey.data.surveyId,
              segments: access.segments,
              isLocked: false,
              placeholders: access.placeholders,
            };

            await api.post('/accesses', accessPost);
          });
        }

        navigate(`/surveys/${responseSurvey.data.surveyId}`);
      } catch (err) {
        if (err instanceof Yup.ValidationError) {
          const errors = getValidationErrors(err);

          formRef.current?.setErrors(errors);

          return;
        }

        addToast({
          type: 'error',
          title: 'Registration Error',
          description:
            'An error occurred while creating the survey, please try again.',
        });
      }
    },
    [addToast, navigate, state.projectId],
  );

  return (
    <>
      <h1>New Survey</h1>

      <FormProjectSurvey
        formRef={formRef}
        handleSubmit={handleSubmit}
        buttonText="Next"
        projectId={Number(state?.projectId)}
      ></FormProjectSurvey>
    </>
  );
};

export default CreateProjectSurvey;
